// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./SpecLibrary.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";

// MUI ICONS
import DownloadIcon from "@mui/icons-material/Download";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import NewSpecDialog from "./NewSpecDialog/NewSpecDialog";
import TanStackTable from "../../Misc/Table/TanStackTable";
import DownloadSingleSpecDialog from "./DownloadSingleSpecDialog/DownloadSingleSpecDialog";

// CONSTANTS
import {
	checkboxSort,
	dateSort,
	defaultSort,
	notApplicable,
	strictSort,
} from "../../Misc/Table/TanStackTableConstants";
import { convertToPST } from "../../../constants/dateTime";

// OTHER

// REACT COMPONENT
const SpecLibrary = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [specLibraryRecords, setSpecLibraryRecords] = useState([]);
	const [specLibraryRecordRows, setSpecLibraryRecordRows] = useState([]);
	const [specLibraryRecordColumns, setSpecLibraryRecordColumns] = useState(
		[]
	);
	const [statuses, setStatuses] = useState([]);

	const [
		documentGenerationOptionsDialogOpen,
		setDocumentGenerationOptionsDialogOpen,
	] = useState(false);
	const [selectedSpecification, setSelectedSpecification] = useState({});

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// Dialog
	const [newSpecDialogOpen, setNewSpecDialogOpen] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		// Fetch Spec Library Records
		fetchSpecLibraryRecords();

		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Spec Library",
			employeeID: employeeID,
			notes: "Accessed the Spec Library page",
		});
	}, []);

	useEffect(() => {
		if (specLibraryRecords.length > 0 && statuses.length > 0) {
			createRowData();
			createColumnData();
		}
	}, [specLibraryRecords, statuses]);

	// INPUT HANDLERS
	// Dialog
	const newSpecDialogOpened = () => {
		setNewSpecDialogOpen(true);
	};

	const newSpecDialogClosed = () => {
		setNewSpecDialogOpen(false);
	};

	const handleDownloadClicked = async (record) => {
		setSelectedSpecification(record);
		setDocumentGenerationOptionsDialogOpen(true);
	};

	const handleDownloadClosed = () => {
		setDocumentGenerationOptionsDialogOpen(false);
		setSelectedSpecification({});
	};

	// API FUNCTIONS

	const fetchSpecLibraryRecords = async () => {
		setIsLoading(true);
		await getSpecificationRecords();
		await fetchStatuses();

		setIsLoading(false);
	};

	const getSpecificationRecords = async (sortBy, sortDirection) => {
		let queryParams;
		if (sortBy && sortDirection) {
			queryParams = `sortBy=${sortBy}&sortDirection=${sortDirection}`;
		}

		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				`/api/specifications/allStandardSpecifications?${queryParams}`
			);

			setSpecLibraryRecords(res.data);
		} catch (error) {
			console.log("Error in fetchSpecLibraryRecords: ", error);
		}
	};

	const fetchStatuses = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/specifications/statuses/all"
			);
			setStatuses(res.data);
		} catch (error) {
			console.log("Error in fetchStatuses: ", error);
		}
	};

	// HELPER FUNCTIONS
	const createColumnData = () => {
		setSpecLibraryRecordColumns([
			{
				accessorKey: "division",
				header: () => <span>Division</span>,
				label: "Division",
				cell: (props) => <p>{props.getValue() ?? notApplicable}</p>,
				filterType: "textfield",
				filterFn: strictSort,
			},
			{
				accessorKey: "title",
				header: () => <span>Title</span>,
				label: "Title",
				cell: ({ row }) =>
					row.original.id && row.original.title ? (
						<a
							href={`/design/spec_library/${row.original.id}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							{row.original.title}
						</a>
					) : (
						notApplicable
					),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "description",
				header: () => <span>Description</span>,
				label: "Description",
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "status_name",
				header: () => <span>Status</span>,
				label: "Status",
				cell: ({ row }) =>
					row.original.status_name && row.original.status_color ? (
						<span style={{ color: row.original.status_color }}>
							{row.original.status_name}
						</span>
					) : (
						notApplicable
					),
				filterType: "checkbox",
				filterOptions: statuses.map((status) => ({
					id: status.id,
					label: status.name,
				})),
				filterFn: checkboxSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Created</span>,
				label: "Created",
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "updated",
				header: () => <span>Last Updated</span>,
				label: "Last Updated",
				cell: (props) => <p>{props.getValue() || notApplicable}</p>,
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "download",
				header: "",
				label: "Download",
				cell: ({ row }) => (
					<IconButton
						onClick={() =>
							handleDownloadClicked(row.original.record)
						}
						size="small"
						disableRipple={true}
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<DownloadIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const createRowData = () => {
		setSpecLibraryRecordRows(
			specLibraryRecords.map((record) => ({
				id: record.id,
				division: record.division,
				title: record.title,
				description: record.description,
				status_name: record.status_name,
				status_color: record.status_color,
				created: convertToPST(record.created),
				updated: convertToPST(record.modified),
				record: record,
			}))
		);
	};

	// RENDER
	return (
		<div className="SpecLibrary">
			<div className="specLibraryHeader">
				<div className="specLibraryHeaderTitle">
					Spec Library
					{props.isAdmin ? (
						<Button
							variant="contained"
							color="primary"
							onClick={newSpecDialogOpened}
							disabled={isLoading}
						>
							New Spec
						</Button>
					) : null}
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="specLibraryTableContainerDiv">
					{specLibraryRecords.length === 0 && <J2NoResultsFound />}
					{specLibraryRecords.length > 0 &&
						specLibraryRecordRows.length > 0 &&
						specLibraryRecordColumns.length > 0 && (
							<TanStackTable
								columnData={specLibraryRecordColumns}
								rowData={specLibraryRecordRows}
							/>
						)}
				</div>
			)}
			<NewSpecDialog
				open={newSpecDialogOpen}
				newSpecDialogClosed={newSpecDialogClosed}
				statuses={statuses}
			/>
			<DownloadSingleSpecDialog
				open={documentGenerationOptionsDialogOpen}
				DownloadSingleSpecDialogClosed={() => handleDownloadClosed()}
				selectedSpecification={selectedSpecification}
			/>
		</div>
	);
};

// EXPORT
export default SpecLibrary;
